import React, { useState } from "react";
import { useLanguage } from "../contexts/LanguageContext";
import emailjs from "emailjs-com";
import LeftSVG from "../assets/page_svg/vector.svg";
import RightSVG from "../assets/page_svg/Contact.svg";

// Import fonts and TailwindCSS
import "@fontsource/nunito";
import "@fontsource/fredoka";
import Footer from "../components/Footer";
import WhatsAppButton from "../components/WhatsappButton";
import RondleidingForm from "../components/RondleidingForm";

const Contact: React.FC = () => {
  const { t } = useLanguage();
  const [error, setError] = useState("");
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [openForm, setOpenForm] = React.useState<null | "rondleiding">(null);
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });

  const SERVICE_ID = process.env.REACT_APP_EMAILJS_SERVICE_ID || "";
  const TEMPLATE_ID = process.env.REACT_APP_EMAILJS_REGISTER_TEMPLATE_ID || "";
  const USER_ID = process.env.REACT_APP_EMAILJS_USER_ID || "";

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    setEmailSuccess(null);
    setEmailError(null);

    const fullName = `${formData.firstName} ${formData.lastName}`;

    // 🧠 Build content dynamically
    const content = `
      Contact Form Submission
      -----------------------
      Name: ${fullName}
      Email: ${formData.email}
      Phone: ${formData.phone}
      Subject: ${formData.subject}
      Message:
      ${formData.message}  `;


    try {
      // Send email via EmailJS
      await emailjs.send(
        SERVICE_ID,
        TEMPLATE_ID,
        {
          form_type: "Contact Page",
          content: content,
        },
        USER_ID
      );
      setEmailSuccess(
        t("form.sendSuccess") || "Registration email sent successfully!"
      );

      setFormData({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
      });

      setTimeout(() => {
        setEmailSuccess(null);
      }, 3000);
    } catch (err) {
      setError(t("auth.registrationFailed"));
      setEmailError(
        t("form.sendError") || "Failed to send registration email."
      );
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#F9DCAC" }}>
      {/* Hero Section */}
      <div className="relative z-10">
        <section className="relative py-20 px-4 sm:px-6 lg:px-8 hero-section-h">
          {/* Decorative SVGs (scaled with container) */}
          <div className="absolute inset-0 pointer-events-none z-0">
            {/* Left SVG */}
            <img
              src={LeftSVG}
              alt="left decoration"
              className="absolute top-0 left-0 max-w-[150px] sm:max-w-[220px] lg:max-w-[280px] w-full opacity-100"
            />
            {/* Right SVG */}
            <img
              src={RightSVG}
              alt="right decoration"
              className="absolute bottom-0 right-0 max-w-[220px] sm:max-w-[220px] md:max-w-[250px] lg:max-w-[400px] w-full opacity-100"
            />
          </div>
          <div className="max-w-6xl mx-auto text-center z-10 relative">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-child font-semibold text-black mb-6">
              {t("contact.title")}
            </h1>
            <p className="text-xl md:text-2xl text-neutral-800 max-w-3xl mx-auto leading-relaxed">
              {t("contact.subtitle")}
            </p>
          </div>
        </section>

        {/* Contact Information Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-6xl mx-auto">
            <div className="grid lg:grid-cols-2 gap-12">
              {/* Contact Form */}
              <div className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft">
                <h2 className="text-3xl font-child font-semibold text-neutral-900 mb-6">
                  {t("common.sendMessage")}
                </h2>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="firstName"
                        className="block text-sm font-medium text-neutral-800 mb-2"
                      >
                        {t("form.firstname")}
                      </label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        placeholder={t("form.firstnamePlaceholder")}
                        required
                        className="w-full px-4 py-3 border border-neutral-300 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="lastName"
                        className="block text-sm font-medium text-neutral-800 mb-2"
                      >
                        {t("form.lastname")}
                      </label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        placeholder={t("form.lastnamePlaceholder")}
                        required
                        className="w-full px-4 py-3 border border-neutral-300 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
                      />
                    </div>
                  </div>
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-neutral-800 mb-2"
                    >
                      {t("common.email")}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder={t("form.emailPlaceholder")}
                      required
                      className="w-full px-4 py-3 border border-neutral-300 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-neutral-800 mb-2"
                    >
                      {t("common.phone")}
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      placeholder={t("form.phonePlaceholder")}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-neutral-300 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300"
                    />
                  </div>
                  <div>
                    <label
                      htmlFor="subject"
                      className="block text-sm font-medium text-neutral-800 mb-2"
                    >
                      {t("form.subject")}
                    </label>
                    <select
                      id="subject"
                      name="subject"
                      value={formData.subject}
                      onChange={handleInputChange}
                      required
                      className="w-full appearance-none px-4 py-3 border border-neutral-300 rounded-2xl focus:ring-2 focus:ring-[#492A19] focus:border-transparent transition-all duration-300"
                      style={{
                        backgroundImage: `url("data:image/svg+xml;utf8,<svg fill='none' stroke='%23492A19' stroke-width='2' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'><path d='M19 9l-7 7-7-7'/></svg>")`,
                        backgroundRepeat: "no-repeat",
                        backgroundPosition: "right 1rem center", // 👈 aligns to the right side
                        backgroundSize: "1.25rem",
                      }}
                    >
                      <option value="">{t("form.selectSubject")}</option>
                      <option value="general">
                        {t("form.generalInquiry")}
                      </option>
                      <option value="enrollment">
                        {t("form.registration")}
                      </option>
                      <option value="schedule">
                        {t("form.scheduleVisit")}
                      </option>
                      <option value="pricing">{t("form.pricingInfo")}</option>
                      <option value="other">{t("form.other")}</option>
                    </select>
                  </div>
                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-neutral-800 mb-2"
                    >
                      {t("form.message")}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      placeholder={t("form.messagePlaceholder")}
                      required
                      rows={6}
                      className="w-full px-4 py-3 border border-neutral-300 rounded-2xl focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all duration-300 resize-none"
                    ></textarea>
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-[#FBEACD] text-[#492A19] font-child font-semibold py-3 px-8 rounded-full border-2 hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300"
                    style={{ borderColor: "#492A19" }}
                  >
                    {t("common.sendMessage")}
                  </button>

                  {error && (
                    <div className="bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-2xl font-medium">
                      {error}
                    </div>
                  )}
                  {emailSuccess && (
                    <div className="bg-green-50 border-2 border-green-200 text-green-700 px-6 py-4 rounded-2xl font-medium mt-4">
                      {emailSuccess}
                    </div>
                  )}
                  {emailError && (
                    <div className="bg-red-50 border-2 border-red-200 text-red-700 px-6 py-4 rounded-2xl font-medium mt-4">
                      {emailError}
                    </div>
                  )}
                </form>
              </div>

              {/* Contact Information */}
              <div className="space-y-8">
                <div className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft">
                  <h3 className="text-2xl font-child font-semibold text-neutral-800 mb-6">
                    {t("common.getInTouch")}
                  </h3>
                  <div className="space-y-6">
                    <div className="flex items-center justify-center">
                      <div>
                        <h4 className="font-child font-semibold text-neutral-800 mb-2">
                          {t("common.address")}
                        </h4>
                        <p className="text-neutral-600">
                          Lindenhof 3,
                          <br />
                          3442 GT Woerden
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start justify-center">
                      <div>
                        <h4 className="font-child font-semibold text-neutral-800 mb-2">
                          {t("common.phone")}
                        </h4>
                        <p className="text-neutral-600">
                          +31 6 16504171
                        </p>
                      </div>
                    </div>
                    <div className="flex items-start justify-center">
                      <div>
                        <h4 className="font-child font-semibold text-neutral-800 mb-2">
                          {t("common.email")}
                        </h4>
                        <a href="mailto:info@mini-avonturiers.nl" className="text-neutral-600 no-underline">
                          {t("contact.emailText")}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="bg-[#FBEACD] rounded-3xl p-8 shadow-soft">
                  <h3 className="text-2xl font-child font-semibold text-neutral-800 mb-6">
                    {t("common.openingHours")}
                  </h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-neutral-600">
                        {t("contact.mondayFriday")}
                      </span>
                      <span className="font-medium text-neutral-800">
                        07:00 - 18:30
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-600">
                        {t("contact.saturday")}
                      </span>
                      <span className="font-medium text-neutral-800">
                        {t("contact.saturdayOpeningHours")}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-neutral-600">
                        {t("contact.sunday")}
                      </span>
                      <span className="font-medium text-neutral-800">
                        {t("contact.closed")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-20 px-4 sm:px-6 lg:px-8 font-child">
          <div className="max-w-4xl mx-auto text-center bg-[#FBEACD] rounded-3xl p-12">
            <h2 className="text-3xl md:text-4xl font-child font-semibold text-black mb-6">
              {t("common.ctaReady")}
            </h2>
            <p className="text-xl text-neutral-800 mb-8 leading-relaxed">
              {t("common.ctaDescription")}
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <button
                className="inline-flex items-center justify-center px-8 py-3 bg-[#FBEACD] text-[#492A19] font-child font-semibold text-lg rounded-full shadow-warm hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300 transform hover:scale-105 border-2"
                style={{ borderColor: "#492A19" }}
                onClick={() => setOpenForm("rondleiding")}
              >
                {t("common.ctaScheduleVisit")}
              </button>
              <button
                className="inline-flex items-center justify-center px-8 py-3 bg-[#FBEACD] text-[#492A19] font-child font-semibold text-lg rounded-full border-2 hover:bg-[#492A19] hover:text-[#F9DCAC] transition-all duration-300 transform hover:scale-105"
                style={{ borderColor: "#492A19" }}
              >
                {t("common.ctaDownloadBrochure")}
              </button>
            </div>
          </div>
        </section>
      </div>
            {/* Forms Modals */}
      {openForm === "rondleiding" && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 px-4 sm:px-6">
          <div className="bg-[#FBEACD] rounded-2xl p-8 max-w-lg w-full relative">
            <button
              onClick={() => setOpenForm(null)}
              className="absolute top-4 right-4 text-2xl"
            >
              &times;
            </button>
            <RondleidingForm />
          </div>
        </div>
      )}
      {/* Footer */}
      {/* WhatsApp Floating Contact Button */}
      <WhatsAppButton />
      <Footer />
    </div>
  );
};

export default Contact;
